import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  isAstroSystem,
  periodKeyFor,
  type HoroscopePeriod,
  type Signal,
} from '@astrally/shared';
import type { HoroscopeResult } from '@astrally/astro-engine';
import { useApp, type ChatEntry } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { localdb } from '../../lib/localdb';
import { ScreenHeader, Section } from '../../components/common';
import { useAccuracy, type RateVerdict, type SyncContext } from './accuracy';
import { compareReadings } from './compare';
import {
  HOROSCOPE_DISCLAIMER,
  PERIOD_TABS,
  PERIOD_TAB_LABELS,
  buildDayView,
  buildReading,
  deviceTimezone,
  periodLabel,
  readerPlace,
  type ComputedReading,
  type ReadingOutcome,
  type SystemTab,
} from './horoscopeData';
import { DivergenceCard } from './DivergenceCard';
import { PanchangaCard } from './PanchangaCard';
import { ReadingCard, type RatingBridge } from './ReadingCard';
import {
  BOTH_LABEL,
  CAST_FOR,
  NARRATE_BUSY,
  NARRATE_CACHED,
  NARRATE_CTA,
  NARRATE_HINT,
  PERIOD_GROUP_LABEL,
  SECTION_DIVERGENCE,
  SECTION_NARRATION,
  SECTION_PANCHANGA,
  SECTION_READING,
  SYSTEM_BLURB,
  SYSTEM_GROUP_LABEL,
  movedAwayNote,
  narrationPrompt,
  systemName,
} from './copy';

/**
 * The horoscope surface — four windows, two traditions, and an honest account
 * of where they disagree.
 *
 * THREE RULES THIS SCREEN IS BUILT AROUND
 *
 * 1. DETERMINISTIC CONTENT RENDERS INSTANTLY, AND NOTHING GENERATES ON MOUNT.
 *    Every reading, every signal and the whole panchanga come out of
 *    `@astrally/astro-engine` on this device — a few milliseconds, no network,
 *    no AI budget. Flipping between Today / Week / Month / Year or between the
 *    traditions costs nothing and works on a plane. The ONLY thing here that
 *    spends one of the ten free daily messages is the narration button, and it
 *    is only ever pressed deliberately; its answer is then cached for the rest
 *    of the calendar day so reopening this screen never spends a second one.
 *
 * 2. JYOTISH IS THE DEFAULT AND COMES FIRST. Astrally is an Indian-first app;
 *    Western is the opt-in side path. Each side is wrapped in `data-mode`, so
 *    the warm Jyotish palette and the cool Western one are visible before a
 *    single label is read.
 *
 * 3. THE APP REFUSES RATHER THAN GUESSES. A Jyotish period reading is counted
 *    in signs from the natal Moon; when an uncertain birth time leaves that
 *    Moon in either of two signs the data layer returns `moon-sign-uncertain`
 *    and this screen says so in Home's own words. It never picks a sign.
 *
 * The comparison in "Both" is the differentiator: two zodiacs roughly 24°
 * apart after centuries of precession, read from two different anchors — your
 * Moon on one side, your whole chart on the other.
 */

/** Local calendar day, used only to expire the narration cache. Never UTC. */
function localDayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const NARRATION_PREFIX = 'horoNarration:';

const SYSTEM_TABS: SystemTab[] = ['vedic', 'western', 'both'];

function tabLabel(tab: SystemTab): string {
  return tab === 'both' ? BOTH_LABEL : systemName(tab);
}

/** Home deep-links `/horoscope?system=vedic`. Anything else falls back to Jyotish. */
function initialTab(search: string): SystemTab {
  const raw = new URLSearchParams(search).get('system');
  if (raw === 'both') return 'both';
  return isAstroSystem(raw) ? raw : 'vedic';
}

export function HoroscopeScreen() {
  const { t, locale } = useI18n();
  const app = useApp();
  const location = useLocation();
  // Destructured rather than held as an object: `useAccuracy` returns a fresh
  // literal every render, so depending on it would rebuild the rating bridge
  // (and every rate button under it) on each keystroke elsewhere in the tree.
  const { rate, verdictFor } = useAccuracy();

  const [period, setPeriod] = useState<HoroscopePeriod>('day');
  const [tab, setTab] = useState<SystemTab>(() => initialTab(location.search));
  const [narration, setNarration] = useState<ChatEntry | null>(null);
  const [narrating, setNarrating] = useState(false);
  /** What the narration button says when it cannot act. Never a disabled button. */
  const [narrateNotice, setNarrateNotice] = useState('');

  /**
   * Home deep-links `/horoscope?system=vedic` from each tradition's card. The
   * tab is component state after that, so this only re-runs when the URL
   * itself changes — arriving from a different card switches the tab, tapping
   * the control does not fight it.
   */
  useEffect(() => {
    setTab(initialTab(location.search));
  }, [location.search]);

  // One instant for the whole visit, so a reading and its panchanga can never
  // describe two different days because the clock ticked mid-render.
  const now = useMemo(() => new Date(), []);

  /**
   * Where the reading is cast. Astrally holds exactly one coordinate — the
   * birth place — because it never asks the device for a location, so that is
   * both the sunrise and the civil day. `movedAway` is how the UI stays honest
   * when the reader has since moved somewhere else.
   */
  const place = useMemo(() => (app.chart ? readerPlace(app.chart) : null), [app.chart]);
  const movedAway = useMemo(() => {
    if (!place) return null;
    const device = deviceTimezone();
    return device === place.timezone ? null : device;
  }, [place]);

  const periodKey = useMemo(
    () => (place ? periodKeyFor(period, now, place.timezone) : ''),
    [place, period, now],
  );
  const windowLabel = useMemo(
    () => (place ? periodLabel(period, periodKey, place.timezone, locale) : ''),
    [place, period, periodKey, locale],
  );

  const showVedic = tab === 'vedic' || tab === 'both';
  const showWestern = tab === 'western' || tab === 'both';

  /**
   * Readings are built only for the traditions actually on screen — a year
   * reading samples the ephemeris seventy-odd times, and computing the hidden
   * side would double that for nothing. `buildReading` memoises by chart,
   * place, ayanamsa, system and period, so flipping back is free.
   */
  const vedic = useMemo<ReadingOutcome | null>(
    () =>
      showVedic && place
        ? buildReading({
            chart: app.chart,
            system: 'vedic',
            period,
            place,
            ayanamsaId: app.ayanamsaId,
            at: now,
          })
        : null,
    [showVedic, place, app.chart, period, app.ayanamsaId, now],
  );

  const western = useMemo<ReadingOutcome | null>(
    () =>
      showWestern && place
        ? buildReading({
            chart: app.chart,
            system: 'western',
            period,
            place,
            ayanamsaId: app.ayanamsaId,
            at: now,
          })
        : null,
    [showWestern, place, app.chart, period, app.ayanamsaId, now],
  );

  /** Derived from the two computed readings and the chart. Nothing generated. */
  const divergence = useMemo(() => {
    if (tab !== 'both' || !app.chart) return null;
    if (!vedic?.ok || !western?.ok) return null;
    return compareReadings(vedic.reading, western.reading, app.chart);
  }, [tab, app.chart, vedic, western]);

  /** Panchanga + rahu kaal / abhijit. Calendar maths — no chart, no AI. */
  const dayView = useMemo(
    () => (place && period === 'day' && showVedic ? buildDayView(place, app.ayanamsaId, now) : null),
    [place, period, showVedic, app.ayanamsaId, now],
  );

  /* --- rating ------------------------------------------------------------- */

  /**
   * Everything `horoscopeGet` needs to reproduce a reading server-side before a
   * rating can be filed against it. Null simply means the rating stays local,
   * which accuracy.ts already treats as a first-class outcome.
   */
  const syncContext = useMemo<SyncContext | null>(() => {
    if (!app.birthProfile || !app.chart || !place) return null;
    return {
      birth: app.birthProfile.input,
      here: place,
      ayanamsaId: app.ayanamsaId,
      houseSystem: app.chart.meta.houseSystem,
    };
  }, [app.birthProfile, app.chart, place, app.ayanamsaId]);

  const onRate = useCallback(
    (reading: ComputedReading, signal: Signal, verdict: RateVerdict) => {
      if (!syncContext) return;
      // Synchronous by contract — accuracy.ts lands the verdict in state before
      // this returns and only then syncs in the background.
      rate({
        reading,
        signalId: signal.id,
        signalLabel: signal.label,
        area: signal.area,
        verdict,
        ctx: syncContext,
      });
    },
    [rate, syncContext],
  );

  const rating = useMemo<RatingBridge | null>(
    () => (syncContext ? { verdictFor, onRate } : null),
    [syncContext, verdictFor, onRate],
  );

  /* --- narration: the only paid thing on this screen ----------------------- */

  const day = localDayKey(now);
  const narrationKey =
    app.uid && periodKey ? `${NARRATION_PREFIX}${app.uid}:${day}:${tab}-${period}-${periodKey}` : null;

  /**
   * Reading the cache is the ONLY thing that happens on mount. Yesterday's
   * narrations are swept at the same time — they accrue one row per day per
   * view forever if nobody clears them.
   */
  useEffect(() => {
    if (!narrationKey) {
      setNarration(null);
      return;
    }
    let alive = true;
    setNarration(null);
    localdb
      .get<ChatEntry>('kv', narrationKey)
      .then((cached) => {
        if (alive) setNarration(cached ?? null);
      })
      .catch(() => undefined);
    localdb
      .keys('kv')
      .then((keys) =>
        Promise.all(
          keys
            .filter((k) => k.startsWith(NARRATION_PREFIX) && !k.includes(`:${day}:`))
            .map((k) => localdb.del('kv', k)),
        ),
      )
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [narrationKey, day]);

  /** The readings the narration is allowed to talk about — exactly what is shown. */
  const visibleResults = useMemo<HoroscopeResult[]>(() => {
    const out: HoroscopeResult[] = [];
    if (vedic?.ok) out.push(vedic.reading.result);
    if (western?.ok) out.push(western.reading.result);
    return out;
  }, [vedic, western]);

  const narrate = async () => {
    if (!narrationKey || narrating || visibleResults.length === 0) return;
    // The button is never disabled, so it has to answer the tap itself.
    if (app.allyBusy) {
      setNarrateNotice(NARRATE_BUSY);
      return;
    }
    setNarrateNotice('');
    setNarrating(true);
    // sendToAlly only rejects on its own busy guard; a backend failure comes
    // back as an ally message, which is exactly what should be rendered.
    const entry = await app
      .sendToAlly(narrationPrompt(visibleResults, period, dayView?.panchanga ?? null))
      .catch(() => null);
    setNarrating(false);
    if (!entry) return;
    setNarration(entry);
    await localdb.set('kv', narrationKey, entry).catch(() => undefined);
  };

  /** Drops today's cached narration so it can be generated once more. */
  const retryNarration = async () => {
    if (!narrationKey || narrating) return;
    if (app.allyBusy) {
      setNarrateNotice(NARRATE_BUSY);
      return;
    }
    await localdb.del('kv', narrationKey).catch(() => undefined);
    setNarration(null);
    await narrate();
  };

  /* --- render -------------------------------------------------------------- */

  const noChart: ReadingOutcome = {
    ok: false,
    reason: 'no-chart',
    message: t('home.calculating'),
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('nav.horoscope')} subtitle={windowLabel || undefined} back />

      <div className="seg" role="group" aria-label={PERIOD_GROUP_LABEL}>
        {PERIOD_TABS.map((p) => (
          <button
            key={p}
            type="button"
            className={`seg__opt${period === p ? ' seg__opt--on' : ''}`}
            aria-pressed={period === p}
            onClick={() => setPeriod(p)}
          >
            {PERIOD_TAB_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Jyotish first: this app is Jyotish-first, and tab order is a claim. */}
      <div className="seg" role="group" aria-label={SYSTEM_GROUP_LABEL} style={{ marginTop: 8 }}>
        {SYSTEM_TABS.map((s) => (
          <button
            key={s}
            type="button"
            className={`seg__opt${tab === s ? ' seg__opt--on' : ''}`}
            aria-pressed={tab === s}
            onClick={() => setTab(s)}
          >
            {tabLabel(s)}
          </button>
        ))}
      </div>

      <div className="tiny faint" style={{ lineHeight: 1.5, marginTop: 8 }}>
        {SYSTEM_BLURB[tab]}
      </div>

      {place && (
        <div className="tiny faint" style={{ lineHeight: 1.5, marginTop: 6 }}>
          {CAST_FOR} {place.label ?? `${place.lat.toFixed(2)}, ${place.lon.toFixed(2)}`} ·{' '}
          {place.timezone}
        </div>
      )}
      {movedAway && place && (
        <div className="banner banner--gold tiny" style={{ marginTop: 8, lineHeight: 1.5 }}>
          {movedAwayNote(place.label, movedAway)}
        </div>
      )}

      {/* ---- The reading. Deterministic, instant, offline, free. ---- */}
      <Section title={SECTION_READING} term="gochara">
        <div className="col">
          {showVedic && (
            <ReadingCard
              system="vedic"
              outcome={vedic ?? noChart}
              rating={rating}
              periodLabel={windowLabel}
              // In "Both" the Western card is already on screen.
              onShowWestern={tab === 'vedic' ? () => setTab('western') : undefined}
            />
          )}
          {showWestern && (
            <ReadingCard
              system="western"
              outcome={western ?? noChart}
              rating={rating}
              periodLabel={windowLabel}
            />
          )}
        </div>
      </Section>

      {/* ---- Where the two land, and why. The whole point of "Both". ---- */}
      {divergence && (
        <Section title={SECTION_DIVERGENCE} term="precession">
          <DivergenceCard divergence={divergence} />
        </Section>
      )}

      {/* ---- Today's five limbs and its two windows. ---- */}
      {dayView && place && (
        <Section title={SECTION_PANCHANGA} term="panchanga">
          <PanchangaCard
            panchanga={dayView.panchanga}
            windows={dayView.windows}
            place={place}
            now={now}
          />
        </Section>
      )}

      {/* ---- The one paid thing, and only on a tap. ---- */}
      {visibleResults.length > 0 && narrationKey && (
        <Section title={SECTION_NARRATION}>
          <div className="card">
            <div className="tiny faint" style={{ marginBottom: 8, lineHeight: 1.5 }}>
              {NARRATE_HINT}
            </div>
            {narration ? (
              <>
                <div className="small" style={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {narration.text}
                </div>
                <div className="row" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                  <Link to="/ai" className="small accent">
                    {t('home.openChat')} →
                  </Link>
                  {/* A reply that cites nothing is almost always the friendly
                      error copy, and pinning that for a whole day is worse than
                      offering one deliberate re-run. */}
                  {(narration.citedFactIds?.length ?? 0) === 0 && (
                    <button className="chip" onClick={retryNarration}>
                      {t('common.retry')}
                    </button>
                  )}
                </div>
                <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
                  {NARRATE_CACHED}
                </div>
              </>
            ) : narrating ? (
              <div className="row">
                <div className="spinner" />
                <span className="muted small">{t('home.aiThinking')}</span>
              </div>
            ) : (
              <button className="btn btn--ghost btn--small btn--block" onClick={narrate}>
                {NARRATE_CTA}
              </button>
            )}
            {narrateNotice && (
              <div className="banner banner--info tiny" style={{ marginTop: 10, lineHeight: 1.5 }}>
                {narrateNotice}
              </div>
            )}
          </div>
        </Section>
      )}

      <div className="tiny faint" style={{ marginTop: 18, lineHeight: 1.5 }}>
        {HOROSCOPE_DISCLAIMER}
      </div>
      <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
        {t('safety.disclaimer')}
      </div>
    </div>
  );
}
